import React, { useMemo, useState } from 'react';
import { Button, Modal, Checkbox } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useCriticalEvents } from '@src/services/queries';
import { formatEventToActivationFormat } from '@src/shared/utils/formatEvent';
import {
  ValidationMessage,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  CriticalEventSelectionSection,
  ModalActions,
} from './styled';

interface ActivateFlowProps {
  funnelSteps: string[];
  defaultOpen?: boolean;
  onClose?: () => void;
}

const requiredCriticalEventsCount = 2;

const ActivateFlow: React.FC<ActivateFlowProps> = ({ funnelSteps, defaultOpen, onClose }) => {
  const [isActivateFlowModalVisible, setIsActivateFlowModalVisible] = useState(!!defaultOpen);
  const [selectedCriticalEvents, setSelectedCriticalEvents] = useState<(string | number)[]>([]);
  const { data: criticalEventsData, isLoading } = useCriticalEvents();

  const flowCriticalEvents = useMemo(() => {
    if (selectedCriticalEvents.length !== 2) return undefined;

    const [firstSelectedCriticalEvent, secondSelectedCriticalEvent] = selectedCriticalEvents as [
      string,
      string,
    ];

    const firstCriticalEventRule = criticalEventsData?.criticalEventMap.get(
      firstSelectedCriticalEvent,
    );
    const secondCriticalEventRule = criticalEventsData?.criticalEventMap.get(
      secondSelectedCriticalEvent,
    );

    if (!firstCriticalEventRule || !secondCriticalEventRule) return undefined;

    return {
      firstCriticalEventRule,
      secondCriticalEventRule,
    };
  }, [criticalEventsData?.criticalEventMap, selectedCriticalEvents]);

  // Convert funnel steps to checkbox options
  const criticalEventOptions = Array.from(new Set(funnelSteps)).map((step) => ({
    label: isLoading ? (
      <LoadingOutlined />
    ) : (
      (criticalEventsData?.criticalEventMap.get(step)?.name ?? step)
    ),
    value: step,
  }));

  const showActivateFlowModal = () => {
    setIsActivateFlowModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsActivateFlowModalVisible(false);
    setSelectedCriticalEvents([]);
  };

  const handleCriticalEventSelectionChange = (checkedValues: (string | number)[]) => {
    setSelectedCriticalEvents(checkedValues);
  };

  const handleGoToFlowBuilder = () => {
    if (!flowCriticalEvents) return;

    const { firstCriticalEventRule, secondCriticalEventRule } = flowCriticalEvents;

    const selectedCriticalEventsWithEventRules = {
      start_event: formatEventToActivationFormat(firstCriticalEventRule),
      end_event: formatEventToActivationFormat(secondCriticalEventRule),
    };

    // Store selected critical events with their event rules in local storage
    const localStorageKey = 'uj2fb-events';
    localStorage.setItem(localStorageKey, JSON.stringify(selectedCriticalEventsWithEventRules));

    // Navigate to flow builder in new tab
    const flowBuilderUrl = `${window.location.origin}/app/activation/workflow/flow/new?from=${localStorageKey}`;
    window.open(flowBuilderUrl, '_blank');

    // Reset modal state
    setIsActivateFlowModalVisible(false);
    setSelectedCriticalEvents([]);
  };

  // Helper function to render validation messages
  const renderValidationMessage = () => {
    const selectedCount = selectedCriticalEvents.length;
    const remainingCount = requiredCriticalEventsCount - selectedCount;

    if (selectedCount > requiredCriticalEventsCount) {
      return (
        <ValidationMessage type="error">
          Please select only {requiredCriticalEventsCount} critical events
        </ValidationMessage>
      );
    }

    if (selectedCount > 0 && selectedCount < requiredCriticalEventsCount) {
      return (
        <ValidationMessage type="warning">
          Please select {remainingCount} more critical event{remainingCount > 1 ? 's' : ''}
        </ValidationMessage>
      );
    }

    return <ValidationMessage type="warning" />;
  };

  return (
    <>
      {!defaultOpen && (
        <Button type="default" onClick={showActivateFlowModal}>
          Activate Flow
        </Button>
      )}

      <Modal
        title={null}
        open={isActivateFlowModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={520}
        centered
        afterClose={onClose}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Activate Your Flow</ModalTitle>
            <ModalDescription>
              Select exactly {requiredCriticalEventsCount} critical events to activate your
              analytics flow.
            </ModalDescription>
          </ModalHeader>

          <CriticalEventSelectionSection>
            <Checkbox.Group
              options={criticalEventOptions}
              value={selectedCriticalEvents}
              onChange={handleCriticalEventSelectionChange}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            />
            {renderValidationMessage()}
          </CriticalEventSelectionSection>

          <ModalActions>
            <Button onClick={handleModalCancel}>Cancel</Button>
            <Button
              type="primary"
              loading={isLoading}
              disabled={!flowCriticalEvents}
              onClick={handleGoToFlowBuilder}
            >
              Go to Flow Builder
            </Button>
          </ModalActions>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ActivateFlow;
